import { ExtendedGridColDef } from 'types/datagrid';
import {
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { orgPartNoStockLotColumnDef } from 'ui-component/DataGrid/PartColumnDef';
import {
  allocationRenderCell,
  renderPartCell,
} from 'ui-component/DataGrid/Render';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import { OrgPart } from 'types/part';
import { renderPartV2Cell } from 'ui-component/DataGrid/Render/RenderPartV2Cell';
import { V2OrgPartsFieldName } from 'ui-component/DataGrid/PartV2ColumnDef';
import { getApplyFilterFnV2OrgParts } from 'ui-component/DataGrid/FilterOperators';
import _ from 'lodash';

const findV2OrgPart = (row: DecoratedKitRequestLine) => {
  if (!row?.v2OrgPart) {
    return null;
  }

  const foundPart = _.find(row.v2OrgParts, (part) => part.id === row.v2OrgPart);

  return foundPart ?? null;
};

export const columns: ExtendedGridColDef[] = [
  {
    field: 'allocatedQuantity',
    headerName: 'Allocated Quantity',
    description: 'Sum of allocation quantities for this line',
    type: 'number',
    minWidth: 160,
    valueGetter: (params: GridValueGetterParams) =>
      params.row?.allocatedQuantity ?? 0,
    renderCell: (
      params: GridRenderCellParams<string, DecoratedKitRequestLine>
    ) =>
      allocationRenderCell({
        params,
        allocated: params.row?.allocatedQuantity ?? 0,
        consumed: 0,
        total: params.row?.targetQuantity ?? 0,
        minimumTotal: params.row?.minimumQuantity ?? 0,
      }),
  },
  {
    ...orgPartNoStockLotColumnDef,
    field: V2OrgPartsFieldName,
    headerName: 'Part',
    description: 'Part on the kit request line',
    valueGetter: (params: GridValueGetterParams) => {
      const foundPart = findV2OrgPart(params.row as DecoratedKitRequestLine);
      return foundPart?.mpn;
    },
    getApplyQuickFilterFn: (value) => getApplyFilterFnV2OrgParts(value),
    renderCell: (
      params: GridRenderCellParams<string, DecoratedKitRequestLine>
    ) => {
      const foundV2OrgPart = findV2OrgPart(params.row);
      return foundV2OrgPart
        ? renderPartV2Cell({
            orgPart: foundV2OrgPart,
            altCount: (params.row?.altV2OrgParts ?? []).length,
            unapprovedAltCount: (params.row?.unapprovedAltV2OrgParts ?? [])
              .length,
          })
        : renderPartCell({
            part: params.row.orgPart,
            noPart: null,
            altCount: (
              params.row.orgParts?.filter((part: OrgPart) =>
                (params.row.altParts || []).includes(part.id)
              ) || []
            ).length,
            stockLot: null,
            searchParams: undefined,
            unapprovedAltCount: (
              params.row.orgParts?.filter((part: OrgPart) =>
                (params.row.unapprovedAltParts || []).includes(part.id)
              ) || []
            ).length,
            params,
          });
    },
  },
  {
    field: 'minimumQuantity',
    headerName: 'Minimum',
    description: 'Minimum quantity required for this line',
    type: 'number',
    minWidth: 120,
  },
  {
    field: 'targetQuantity',
    headerName: 'Target',
    description: 'Target quantity for this line',
    type: 'number',
    minWidth: 120,
  },
];
